import { type MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { isCoach } from "../../utils/user-helpers";
import Modal from "../modal-new";
import SvgIcon from "../SvgIcon";

const SEEN_KEY = "clientConfigAnnouncementShown";
// Only coaches created before launch ever used Client Tasks.
const LAUNCH_DATE = new Date("2026-09-14T00:00:00Z");
export const LOOM_URL =
  "https://www.loom.com/share/34ee0993725c4beb84aab19410962088";
const TEMPLATES_URL =
  "/coaching-portal/all-templates?owner=coach&tabView=client-configuration-tab&templateView=client-configuration&modal[type]=client-configuration-templates";
const WHATS_NEW = ["whatsNew1", "whatsNew2", "whatsNew3", "whatsNew4"];

// The user schema has no createdAt; an ObjectId's first 4 bytes are its creation time.
const createdBeforeLaunch = (user) =>
  parseInt(String(user._id).slice(0, 8), 16) * 1000 < LAUNCH_DATE.getTime();

export const isAnnouncementPending = (user) =>
  isCoach(user) && !user.welcomeVideos?.[SEEN_KEY] && createdBeforeLaunch(user);

const markSeen = () =>
  fetch("/actions/user/video-shown", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ type: SEEN_KEY }),
  });

export const ClientConfigAnnouncementModal = ({ activeUser }) => {
  const { t } = useTranslation("athlete-portal");
  const [isVisible, setIsVisible] = useState(
    () => !!activeUser && isAnnouncementPending(activeUser)
  );

  if (!isVisible) return null;

  const handleClose = (e: MouseEvent) => {
    e.preventDefault();
    setIsVisible(false);
    markSeen();
  };

  // The <a> navigates client-side via Rill, so the page never unloads and
  // the request doesn't need awaiting.
  const handleSetUpTemplate = () => {
    setIsVisible(false);
    markSeen();
  };

  return (
    <Modal
      className="client-config-announcement-modal"
      header={false}
      onClose={handleClose}
    >
      <img
        className="announcement-image"
        src="/img/client-config/announcement-modal.webp"
        alt=""
      />
      <div className="announcement-content">
        <button
          type="button"
          className="announcement-close"
          data-button-type="tertiary"
          data-icon-only="true"
          aria-label={t("clientConfiguration.announcementModal.closeAria")}
          onClick={handleClose}
        >
          <img src="/img/new-icons/red-cross.svg" alt="" />
        </button>
        <h2>{t("clientConfiguration.announcementModal.title")}</h2>
        <p className="announcement-subhead">
          {t("clientConfiguration.announcementModal.subhead")}
        </p>
        <p>{t("clientConfiguration.announcementModal.body")}</p>
        <p className="announcement-subhead">
          {t("clientConfiguration.announcementModal.whatsNewTitle")}
        </p>
        <ul>
          {WHATS_NEW.map((key) => (
            <li key={key}>
              <SvgIcon src="/img/new-icons/check-circle.svg" style={{}} />
              <span>{t(`clientConfiguration.announcementModal.${key}`)}</span>
            </li>
          ))}
        </ul>
        <footer>
          <div>
            <p>{t("clientConfiguration.announcementModal.videoLine")}</p>
            <a
              className="announcement-video-link"
              href={LOOM_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/img/new-icons/link.svg" alt="" />
              {LOOM_URL.replace("https://", "")}
            </a>
          </div>
          {/* biome-ignore lint/a11y/useSemanticElements: a real link so Rill navigates client-side; role only picks up button styles */}
          <a
            href={TEMPLATES_URL}
            role="button"
            data-button-type="primary"
            onClick={handleSetUpTemplate}
          >
            {t("clientConfiguration.announcementModal.cta")}
          </a>
        </footer>
      </div>
    </Modal>
  );
};
